/**
 * Settings-Panel — secondary toggles (gear-icon overlay).
 *
 * History:
 *   2026-05-02 (plan v2): all five header buttons (Loop/PiP/Stats/Cast/CopyURL)
 *     collapsed into this panel.
 *   2026-05-03: PiP, Stats and Cast moved back out as prominent icon-buttons in
 *     the bottom Controls-Bar (createControls); the gear panel keeps only the
 *     secondary toggles plus Copy-URL and Tastatur-Hilfe.
 *   2026-05-04 (multi-track plan): added audio-track + subtitle-track <select>
 *     dropdowns at the top of the panel.
 *   2026-05-04 (later): user feedback "die icons sollen nicht im settings,
 *     sondern unten direkt mit rein" — moved audio + subtitle dropdowns OUT
 *     of this panel and into createControls() as first-class bar buttons.
 *     This panel now only carries audionorm/loop/fadeskip toggles + two
 *     action buttons.
 *
 * CSS class .player-overlay__settings is defined in styles/zap.css; positioned
 * as an overlay anchored bottom-right of the stage, just ABOVE the native
 * <video> controls bar.
 */

/**
 * @param {Object} player          ZapPlayer instance — uses setAudioNorm,
 *                                 setLoop, setFadeSkip, openHelp + .video.
 * @param {Object} [opts]
 * @param {Object} [opts.initialValues]    { loop, audionorm, fadeSkip }
 * @param {Object} [opts.features]         feature flags { help }
 * @param {string} [opts.castMediaUrl]     token-signed URL for clipboard (Copy-URL)
 */
export function createSettingsPanel(player, opts = {}) {
  const init = {
    loop:          false,
    audionorm:     false,
    fadeSkip:      true,
    ...(opts.initialValues || {}),
  };
  const features = opts.features || {};
  const castMediaUrl = opts.castMediaUrl || '';

  const panel = document.createElement('div');
  panel.className = 'player-overlay__settings';
  panel.hidden = true;

  panel.innerHTML = `
    <div class="po-st-group">
      <label class="po-st-toggle">
        <input type="checkbox" data-st="audionorm" ${init.audionorm?'checked':''}>
        <span>Audio normalisieren <span class="dim">(Dialog &amp; Action gleich laut)</span></span>
      </label>
      <label class="po-st-toggle">
        <input type="checkbox" data-st="loop" ${init.loop?'checked':''}>
        <span>Auto-Wiederholung</span>
      </label>
      <label class="po-st-toggle">
        <input type="checkbox" data-st="fadeskip" ${init.fadeSkip?'checked':''}>
        <span>Fade beim Überspringen <span class="dim">(0.5 s ein/aus)</span></span>
      </label>
    </div>

    <div class="po-st-actions">
      <button type="button" class="po-st-btn" data-act="copy-url">Stream-URL kopieren</button>
      ${features.help !== false ? '<button type="button" class="po-st-btn" data-act="help">Tastatur-Hilfe</button>' : ''}
    </div>`;

  panel.querySelector('[data-st="audionorm"]')?.addEventListener('change', (e) => player.setAudioNorm(e.target.checked));
  panel.querySelector('[data-st="loop"]')     ?.addEventListener('change', (e) => player.setLoop(e.target.checked));
  panel.querySelector('[data-st="fadeskip"]') ?.addEventListener('change', (e) => player.setFadeSkip(e.target.checked));

  panel.querySelectorAll('.po-st-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const act = btn.dataset.act;
      if (act === 'help') { try { player.openHelp(); } catch {} }
      else if (act === 'copy-url' && castMediaUrl) {
        try { navigator.clipboard?.writeText(castMediaUrl); } catch {}
        try { (window.zapToast || (() => {}))('Stream-URL kopiert'); } catch {}
      }
      panel.hidden = true;
    });
  });

  return {
    panel,
    show()    { panel.hidden = false; },
    hide()    { panel.hidden = true; },
    toggle()  { panel.hidden = !panel.hidden; return !panel.hidden; },
    isVisible() { return !panel.hidden; },
    setLoop(on) {
      const lp = panel.querySelector('[data-st="loop"]');
      if (lp) lp.checked = !!on;
    },
  };
}
